import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Assessment as AnalyticsIcon,
  Sensors as BarriersIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ReportsButton = ({ variant = 'contained', size = 'medium' }) => {
  const { tenantId } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportConfig, setReportConfig] = useState({
    type: 'equipment',
    format: 'pdf',
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleQuickReport = async (type, format) => {
    handleClose();
    await generateReport(type, format);
  };

  const handleCustomReport = () => {
    handleClose();
    setDialogOpen(true);
  };

  const generateReport = async (type, format, options = {}) => {
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      
      const queryString = params.toString();
      const url = `/api/reports/${type}/${format}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Tenant-ID': tenantId
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao gerar relatório');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') ||
                      `${type}_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomReportGenerate = async () => {
    setDialogOpen(false);
    await generateReport(reportConfig.type, reportConfig.format, {
      startDate: reportConfig.startDate,
      endDate: reportConfig.endDate
    });
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
        onClick={handleClick}
        disabled={loading}
      >
        Relatórios
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: { minWidth: 250 }
        }}
      >
        <MenuItem onClick={() => handleQuickReport('equipment', 'pdf')}>
          <ListItemIcon>
            <PdfIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Equipamentos (PDF)" />
        </MenuItem>
        
        <MenuItem onClick={() => handleQuickReport('equipment', 'excel')}>
          <ListItemIcon>
            <ExcelIcon color="success" />
          </ListItemIcon>
          <ListItemText primary="Equipamentos (Excel)" />
        </MenuItem>
        
        <MenuItem onClick={() => handleQuickReport('barriers', 'pdf')}>
          <ListItemIcon>
            <PdfIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Barreiras (PDF)" />
        </MenuItem>
        
        <MenuItem onClick={() => handleQuickReport('barriers', 'excel')}>
          <ListItemIcon>
            <ExcelIcon color="success" />
          </ListItemIcon>
          <ListItemText primary="Barreiras (Excel)" />
        </MenuItem>
        
        <MenuItem onClick={() => handleQuickReport('analytics', 'excel')}>
          <ListItemIcon>
            <AnalyticsIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Analytics (Excel)" />
        </MenuItem>
        
        <MenuItem onClick={handleCustomReport}>
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
          <ListItemText primary="Relatório Personalizado" />
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Relatório Personalizado</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Relatório</InputLabel>
                <Select
                  value={reportConfig.type}
                  onChange={(e) => setReportConfig({...reportConfig, type: e.target.value})}
                >
                  <MenuItem value="equipment">
                    <DevicesIcon sx={{ mr: 1 }} />
                    Equipamentos
                  </MenuItem>
                  <MenuItem value="barriers">
                    <BarriersIcon sx={{ mr: 1 }} />
                    Barreiras
                  </MenuItem>
                  <MenuItem value="analytics">
                    <AnalyticsIcon sx={{ mr: 1 }} />
                    Analytics
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Formato</InputLabel>
                <Select
                  value={reportConfig.format}
                  onChange={(e) => setReportConfig({...reportConfig, format: e.target.value})}
                >
                  <MenuItem value="pdf">
                    <PdfIcon sx={{ mr: 1 }} color="error" />
                    PDF
                  </MenuItem>
                  <MenuItem value="excel">
                    <ExcelIcon sx={{ mr: 1 }} color="success" />
                    Excel
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data Início"
                type="date"
                fullWidth
                value={reportConfig.startDate}
                onChange={(e) => setReportConfig({...reportConfig, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data Fim"
                type="date"
                fullWidth
                value={reportConfig.endDate}
                onChange={(e) => setReportConfig({...reportConfig, endDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleCustomReportGenerate} variant="contained">
            Gerar Relatório
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportsButton;